// Die naechste sinnvolle Verbesserung eines unvollstaendigen Entwurfs
// (ST-PTY-001, CM-CAP-001, CM-PTY-001..002, CM-UX-001..004, NFR-UX-003..004).
//
// WELCHE AKZEPTANZZEILE HIER EINGELOEST WIRD: "Ein unvollstaendiger Entwurf ist
// zulaessig und zeigt die naechste sinnvolle Verbesserung." Die erste Haelfte
// war gebaut -- kein Feld ausser Art und Name ist Pflicht, und ein
// weggelassenes Feld heisst "nicht bekannt" (quick-capture.ts). Die zweite
// Haelfte fehlte: der Erfolgszustand nannte die naechste AUFGABE ("du kannst
// gleich den naechsten erfassen") und nicht die naechste VERBESSERUNG DIESES
// ENTWURFS. Das ist ein Unterschied, den niemand bemerkt, solange er nicht
// benannt ist -- und die Zeile verlangt genau ihn.
//
// WARUM DAS CLIENTSEITIG ENTSTEHT UND KEINEN LESEWEG BRAUCHT: der Bildschirm
// weiss, was er abgeschickt hat, und der Erfolgsfall bestaetigt, dass genau das
// gespeichert wurde. Ein Leseweg, der eine bereits bekannte Auskunft ein
// zweites Mal holt, ist keine Genauigkeit, sondern eine Abhaengigkeit.
//
// Seit dem 01.09.2026 GIBT ES `GET /parties/{partyId}` (ST-SEA-001, erster
// Schnitt) -- der urspruengliche Grund "es gibt keinen Leseweg" ist damit weg,
// die Entscheidung bleibt. Der Vorschlag waere derselbe, und ein zweiter
// Abruf machte den Erfolgszustand von der Erreichbarkeit einer Route abhaengig,
// die er nicht braucht.
//
// GENAU EIN VORSCHLAG, NIE EINE MANGELLISTE. NFR-UX-004 verlangt eine klar
// erkennbare Hauptaktion und progressive Offenlegung, Kapitel 5.2 Nr. 9
// dasselbe fuer die Standardoberflaeche und Nr. 14 ("Calm technology"), dass im
// Vordergrund die naechste fachliche Aufgabe steht. Eine Aufzaehlung aller
// fehlenden Felder ist das Gegenteil: sie verwandelt eine bewusst schmale
// Anlage in einen Vorwurf und laesst offen, was zuerst zu tun ist.
//
// UND KEIN VORSCHLAG, WENN NICHTS SINNVOLLES FEHLT. Ein erfundener Vorschlag
// waere schlechter als keiner: er behauptete einen Mangel, macht die Zusage
// unglaubwuerdig und wuerde beim naechsten Mal ueberlesen.
//
// ---------------------------------------------------------------------------
// DIE ABLEITUNG DER REIHENFOLGE -- eine fachliche Festlegung, keine Geschmacksfrage
// ---------------------------------------------------------------------------
//
// Der Raum der moeglichen Luecken ist geschlossen und klein, weil die Route
// bereits entscheidet, was ueberhaupt entstehen kann (apps/api/src/domain/
// party/quick-capture.ts): eine Person besitzt mindestens einen Namensteil
// (CHECK aus Migration 0002), eine Organisation besitzt ihren Namen
// (`legal_name` NOT NULL). Ein angelegter Entwurf kann deshalb genau dreierlei
// sein: ohne jeden Kontaktweg, mit halbem Personennamen, oder vollstaendig im
// Rahmen dessen, was dieser Bildschirm erfassen kann.
//
//   1. KEIN KONTAKTWEG (`contact_channel`). Quelle: CM-CAP-001 -- das
//      Quick-Capture-Feld nimmt "Name, E-Mail, Telefon, Firma oder freien
//      Text" entgegen; E-Mail und Telefon sind also erfasste Erstklassigkeit
//      und keine Zierde. CM-PTY-005 fuehrt Contact Points als eigenes,
//      typisiertes Objekt. Kapitel 5.2 Nr. 2 ("One-click first: haeufige
//      Aktionen sind direkt aus Kontext, Suche, E-Mail oder Anruf moeglich")
//      benennt, was ohne Kontaktweg gar nicht erst beginnen kann.
//      WARUM ZUERST: ein Entwurf ohne Kontaktweg traegt einen Namen und sonst
//      nichts, mit dem sich arbeiten laesst. Das ist der groesste einzelne
//      Zugewinn, und er ist unabhaengig von der Art des Kontakts -- er gilt
//      fuer Person und Organisation gleich.
//
//   2. HALBER PERSONENNAME (`person_name`). Quelle: CM-PTY-002 -- fuer jeden
//      Subtyp gelten eigene, typisierte Attribute; Vorname und Nachname sind
//      genau diese Attribute der Person. CM-PTY-001 verlangt, dass eine
//      vorlaeufige Party wiedererkennbar bleibt, und CM-CAP-005 entscheidet
//      Dubletten ueber "unterscheidende Merkmale" -- ein halber Name ist das
//      schwaechste unterscheidende Merkmal, das es gibt.
//      WARUM ERST DANACH: der Datensatz ist bereits identifiziert. Die Route
//      leitet `display_name` aus dem ab, was da ist, und ein Nachname allein
//      ist ein Anzeigename und kein Platzhalter. Die Luecke verschlechtert das
//      Wiederfinden, sie verhindert die Nutzung nicht.
//
//   3. SONST KEIN VORSCHLAG. Eine Person mit beiden Namensteilen und einem
//      Kontaktweg, eine Organisation mit Namen und Kontaktweg: hier ist alles
//      erfasst, was dieser Bildschirm erfassen kann.
//
// AUSDRUECKLICH KEIN VORSCHLAG -- der zweite Kontaktweg. CM-PTY-005 erlaubt
// mehrere Kanaele, aber ein Entwurf mit E-Mail ist erreichbar; "trage noch die
// Telefonnummer nach" waere die erste Zeile einer Mangelliste und keine
// Verbesserung. Die Grenze zwischen "sinnvoll" und "moeglich" ist genau hier,
// und sie wird bewusst gezogen.
//
// AUSDRUECKLICH KEIN VORSCHLAG -- die Nichtziele der Story. Kein Vorschlag
// zeigt auf eine Kontextrollen- oder Verlagszuordnung (`CM-PTY-010` gehoert
// `ST-PTY-002` und hat dort noch keinen Schreibweg) und keiner auf ein
// Primaerkennzeichen (ADR-0027, Migration 0023). Beides fuehrt die Story als
// Nichtziel; ein Vorschlag darauf waere eine Zusage, die dieser Slice nicht
// einloest.
//
// Hier stand bis zum 29.08.2026 "DEC-012 ist vertagt". Das war seit dem
// 25.08. falsch -- ADR-0029 hat `DEC-012` entschieden. Am Nichtziel aendert
// das nichts, an seiner Begruendung alles: es fehlt der Schreibweg, nicht die
// Entscheidung. Ebenso nicht: Handelsname, Verkehrsnummer und
// Buchhandelsklassifikation -- die gehoeren CM-PTY-003 und damit ST-PTY-003.

import type { QuickCaptureForm } from './quick-capture.ts';

/**
 * Stabile Kennung des Vorschlags.
 *
 * Sie ist NICHT der Text. Der Text aendert sich mit jeder Redaktion, die
 * Kennung traegt die Probe und die Ableitung oben -- dieselbe Trennung wie
 * zwischen Grundcode und Satz in access.ts und quick-capture.ts.
 */
export type NextImprovementCode = 'contact_channel' | 'person_name';

/**
 * Die naechste sinnvolle Verbesserung, in der Sprache der Oberflaeche.
 *
 * Zwei Saetze und nicht einer: NFR-UX-003 verlangt, dass jeder Schritt Zweck,
 * naechsten sinnvollen Schritt UND Ergebnis zeigt. `gap` ist der Schritt,
 * `benefit` der Zweck samt Wirkung. Ein Satz, der nur den Mangel nennt, waere
 * ein Vorwurf; einer, der nur die Handlung nennt, waere eine Anweisung ohne
 * Grund.
 *
 * BENENNEND UND NICHT BEFEHLEND, und das ist eine bewusste Korrektur. Der
 * Experience-Review vom 28.08.2026 hat den Fall belegt: ein "Trage eine
 * E-Mail-Adresse nach." verspricht ein Bedienelement, das es hier nicht gibt.
 * Der Bearbeitungsflow gehoert ST-PTY-002, der Leseweg ST-SEA-001, und beide
 * stehen auf `draft`. Eine Aufforderung ohne Weg ist eine Sackgasse -- die
 * Nutzerin sucht das Feld, findet keines und glaubt der Zusage beim naechsten
 * Mal nicht mehr. Der Satz nennt deshalb, WAS dem Entwurf fehlt, und der Zweck
 * nennt, was das Nachtragen einbringt. Sobald ST-PTY-002 den Weg baut, darf der
 * Satz wieder auffordern -- dann stimmt er.
 */
export interface NextImprovement {
  readonly code: NextImprovementCode;
  /** Was dem Entwurf fehlt -- genau eines, benannt und nicht befohlen. */
  readonly gap: string;
  /** Warum sie sich lohnt -- ausgehend vom tatsaechlich Erfassten. */
  readonly benefit: string;
}

/**
 * Die Reihenfolge der Pruefung, als Wert und nicht als Reihenfolge von
 * if-Zweigen.
 *
 * Sie ist pruefbar: die Probe kann behaupten, dass ein Entwurf, auf den BEIDE
 * Regeln zutreffen, den Kontaktweg zeigt -- und dass die Aenderung dieser
 * Reihenfolge auffaellt.
 */
export const NEXT_IMPROVEMENT_ORDER: readonly NextImprovementCode[] = Object.freeze([
  'contact_channel',
  'person_name',
]);

/** Was in einem Feld tatsaechlich steht -- dieselbe Trimmung wie im Anfragerumpf. */
function filled(value: string): boolean {
  return value.trim().length > 0;
}

function contactChannelImprovement(form: QuickCaptureForm): NextImprovement | undefined {
  if (filled(form.email) || filled(form.phone)) {
    return undefined;
  }
  return {
    code: 'contact_channel',
    gap: 'Diesem Entwurf fehlt noch ein Kontaktweg — eine E-Mail-Adresse oder eine Telefonnummer.',
    benefit: 'Mit einem Kontaktweg lässt sich der Kontakt später direkt anschreiben oder anrufen.',
  };
}

function personNameImprovement(form: QuickCaptureForm): NextImprovement | undefined {
  if (form.kind !== 'person') {
    return undefined;
  }
  const hasGiven = filled(form.givenName);
  const hasFamily = filled(form.familyName);
  // Genau einer der beiden fehlt. Keiner von beiden ist nicht erreichbar -- die
  // Route weist das mit `field_required` ab, es gaebe also keinen Entwurf.
  if (hasGiven === hasFamily) {
    return undefined;
  }
  return {
    code: 'person_name',
    gap: hasGiven
      ? 'Diesem Entwurf fehlt noch der Nachname.'
      : 'Diesem Entwurf fehlt noch der Vorname.',
    benefit:
      'Mit dem vollständigen Namen findest du die Person später eindeutig wieder und verwechselst sie nicht mit einer ähnlichen.',
  };
}

const RULES: Readonly<Record<NextImprovementCode, (form: QuickCaptureForm) => NextImprovement | undefined>> =
  Object.freeze({
    contact_channel: contactChannelImprovement,
    person_name: personNameImprovement,
  });

/**
 * Die naechste sinnvolle Verbesserung des gerade angelegten Entwurfs, oder
 * keine.
 *
 * Der Rueckgabewert ist EINER oder `undefined` -- nie eine Liste. Das ist die
 * Zusage aus der Ableitung oben, und sie steht in der Signatur, damit ein
 * spaeterer Aufrufer sie nicht versehentlich zu einer Liste erweitert.
 */
export function nextImprovementFor(form: QuickCaptureForm): NextImprovement | undefined {
  for (const code of NEXT_IMPROVEMENT_ORDER) {
    const improvement = RULES[code](form);
    if (improvement !== undefined) {
      return improvement;
    }
  }
  return undefined;
}

/** Die Beschriftung, unter der der Vorschlag auf dem Bildschirm steht. */
export const NEXT_IMPROVEMENT_LABEL = 'Nächste sinnvolle Verbesserung';

/** Was der Erfolgszustand sagt, wenn nichts Sinnvolles fehlt. */
export const NOTHING_TO_IMPROVE = 'Es fehlt nichts Wesentliches. Du kannst gleich den nächsten erfassen.';
